import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Button from '../../components/forms/button.component';
import BgBody from '../../components/layout/bg-body.component';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../hooks/use-session.hook';
import { useEffect, useState } from 'react';
import InvalidFeedback from '../../components/forms/invalid-feedback.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { STORAGE } from '../../constants';

declare const bootstrap: any;

function SelectAccount() {
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const navigate = useNavigate();
  const response = LocalStorageService.get(STORAGE.ACCOUNTS);
  const defaultValues = {
    account_id: ''
  };

  const [accountColors, setAccountColors] = useState<{ [key: string]: string }>({});
  const bgClasses = ['bg-primary', 'bg-success', 'bg-info', 'bg-warning', 'bg-danger'];
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const accountOptions =
    Array.isArray(response) && response.length > 0
      ? response.map((item: any) => ({
          value: item.account._id,
          label: item.account.name
        }))
      : [];

  const submit = async (data: any) => {
    if (!data.account_id) return;
    LocalStorageService.save(STORAGE.SELECTED_ACCOUNT, data);
    navigate('/dashboard');
  };

  function shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // NOSONAR: Safe use of Math.random for UI shuffling only
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  useEffect(() => {
    if (Array.isArray(response)) {
      const newColors: { [key: string]: string } = { ...accountColors };
      const usedColors: string[] = Object.values(accountColors);
      const shuffledColors = shuffleArray(bgClasses);
      let shouldUpdate = false;

      response.slice(0, 5).forEach((item: any) => {
        const accountId = item?.account?._id;

        if (accountId && !newColors[accountId]) {
          const availableColors = shuffledColors.filter((color) => !usedColors.includes(color));
          const selectedColor = availableColors[0] || bgClasses[0];
          newColors[accountId] = selectedColor;
          usedColors.push(selectedColor);
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setAccountColors(newColors);
      }

      const selectedAccountData = LocalStorageService.get(STORAGE.SELECTED_ACCOUNT);
      const selectedId = selectedAccountData?.account_id;
      if (selectedId && !watch('account_id')) {
        setValue('account_id', selectedId);
        setSelectedAccountId(selectedId);
      }
    }
  }, [response, accountColors]);

  useEffect(() => {
    const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));

    const tooltipInstances = tooltipTriggerList.map((el) => new bootstrap.Tooltip(el));

    return () => {
      tooltipInstances.forEach((tooltip) => tooltip.dispose());
    };
  }, [response, accountColors]);

  const selected = watch('account_id');

  return (
    <>
      <BgBody />
      <div className="d-flex flex-column flex-lg-row flex-column-fluid bg-trans">
        <div className="d-flex flex-column flex-lg-row-fluid py-10 bg-p-dark">
          <div className="d-flex flex-center flex-column flex-column-fluid ">
            <div className="w-lg-500px w-400px p-10 p-lg-15">
              <div className="text-center mb-10">
                <img
                  alt="Logo"
                  src="/assets/media/logos/ch-l-dark.svg"
                  className="h-125px h-lg-155px logo-default mb-5 logob"
                />
                <h1 className="text-white mb-3 fs-2qx text-nowrap">
                  {t('switch-account.greeting', { name: session.full_name })}
                </h1>
                <div className="text-muted fw-semibold fs-4">{t('switch-account.title')}</div>
              </div>
              <h2 className="text-center mb-10 text-white required">
                {t('switch-account.account-label')}
              </h2>
              <div>
                {Array.isArray(response) && response.length > 5 ? (
                  <div className="col-12">
                    <Controller
                      control={control}
                      name="account_id"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={accountOptions}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'select-account-custom is-invalid' : 'form-select-custom-dark'
                            }
                            classNamePrefix="select-account-custom"
                            placeholder={t('Seleccionar cuenta')}
                            onChange={(selectedOption: { value: string; label: string } | null) => {
                              onChange(selectedOption ? selectedOption.value : null);
                            }}
                            value={
                              accountOptions.find((language: any) => language.value === value) ||
                              null
                            }
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                ) : (
                  <div className="d-flex justify-content-center">
                    <div className="d-flex justify-content-center gap-6 account-wrapper">
                      {Array.isArray(response) &&
                        response.map((item: any) => {
                          const account = item?.account;
                          const accountName = account?.name || '';
                          const accountId = account?._id || '';
                          const username = accountName;
                          const initials = accountName.substring(0, 1).toUpperCase();
                          const randomBg = accountColors[accountId] || 'bg-primary';
                          const isSelected = selectedAccountId === accountId;

                          return (
                            <div
                              key={accountId}
                              className={`animate-fadeInUp animate-delay-${accountId.to} cursor-pointer`}
                            >
                              <div
                                className={`card card-accounts-dark  cursor-pointer card-hover-n border-0 rounded-4 text-center p-4 position-relative account-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedAccountId(accountId);
                                  setValue('account_id', accountId);
                                }}
                              >
                                <div
                                  className={`rounded-circle d-flex align-items-center justify-content-center mb-3 mx-auto ${randomBg} account-initials`}
                                  title={username}
                                >
                                  {initials}
                                </div>
                                <div
                                  className="text-truncate small fw-semibold fs-5"
                                  title={username}
                                >
                                  {username}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
              <div className="text-center d-flex flex-center flex-row-fluid pt-12 gap-5 ">
                {selectedAccount ? (
                  <>
                    <button
                      type="button"
                      className="btnA btnA-dark w-50 text-white"
                      onClick={() => navigate(-1)}
                    >
                      {t('general.btn-cancel')}
                    </button>
                    <Button
                      type="submit"
                      className="btn btn-lg btn-primary w-220px text-nowrap"
                      disabled={!selected || isSubmitting || !isValid}
                      isLoading={isSubmitting}
                      onClick={() => handleSubmit((data) => submit(data))()}
                    >
                      {t('switch-account.change-account')}
                      {selected
                        ? ` ${
                            accountOptions.find((option) => option.value === selected)?.label || ''
                          }`
                        : ''}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="submit"
                    className="btn btn-lg btn-primary w-220px"
                    disabled={!selected || isSubmitting || !isValid}
                    isLoading={isSubmitting}
                    onClick={() => handleSubmit((data) => submit(data))()}
                  >
                    {t('general.continue')}
                    {selected &&
                      ` ${accountOptions.find((option) => option.value === selected)?.label || ''}`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SelectAccount;
